'use client';
import Link from "next/link";
import { allCourses } from '@/lib/courses-data';
import { quizCategories, getTotalQuizCount } from '@/lib/quiz-categories';
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AnimatedCounter, ParticleBackground, MouseGlow } from "@/components/animations";
import { 
  ArrowRight, BookOpen, Bot, GraduationCap,
  ChevronRight, Anchor, Zap, Trash2, Sparkles,
  Briefcase, Lightbulb, Calculator, Gamepad2, Users,
  ClipboardList, Shield, Target, Heart, MessageSquare
} from "lucide-react";

// 栏目图标映射
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'Anchor': Anchor,
  'Zap': Zap,
  'Trash2': Trash2,
  'Sparkles': Sparkles,
};

// 智能体图标映射
const agentIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'Briefcase': Briefcase,
  'Lightbulb': Lightbulb,
  'Calculator': Calculator,
  'Gamepad2': Gamepad2,
  'Users': Users,
  'Bot': Bot,
};

// 栏目颜色
const categoryColors: Record<string, { 
  border: string; 
  text: string; 
  glow: string;
  gradient: string;
}> = {
  'stable': {
    border: 'border-emerald-500/50',
    text: 'text-emerald-400',
    glow: 'hover:shadow-[0_0_30px_rgba(52,211,153,0.3)]',
    gradient: 'from-emerald-500/20 to-teal-500/20'
  },
  'disrupted': {
    border: 'border-cyan-500/50',
    text: 'text-cyan-400',
    glow: 'hover:shadow-[0_0_30px_rgba(34,211,238,0.3)]',
    gradient: 'from-cyan-500/20 to-blue-500/20'
  },
  'eliminated': {
    border: 'border-red-500/50',
    text: 'text-red-400',
    glow: 'hover:shadow-[0_0_30px_rgba(239,68,68,0.3)]',
    gradient: 'from-red-500/20 to-pink-500/20'
  },
  'created': {
    border: 'border-purple-500/50',
    text: 'text-purple-400',
    glow: 'hover:shadow-[0_0_30px_rgba(168,85,247,0.3)]',
    gradient: 'from-purple-500/20 to-indigo-500/20'
  },
};

// 静态分类数据（服务端渲染）
const staticCategories = [
  {
    id: 'stable',
    name: '不变',
    slug: 'stable',
    description: '人仍是决策核心，AI只是增强器。战略决策、关键客户关系、组织文化——本质不变，效率提升。',
    icon: 'Anchor',
  },
  {
    id: 'disrupted',
    name: '颠覆',
    slug: 'disrupted',
    description: '原流程被重构，人从执行者变成指挥者。内容生产、开发、客服、数据分析——岗位减少但能力要求更高。',
    icon: 'Zap',
  },
  {
    id: 'eliminated',
    name: '删除',
    slug: 'eliminated',
    description: '不再需要人来做。初级客服、数据录入、基础翻译、低端文案——岗位消失，转为系统能力。',
    icon: 'Trash2',
  },
  {
    id: 'created',
    name: '新增',
    slug: 'created',
    description: '过去不存在的岗位，围绕AI系统运转而生。AI产品经理、Agent设计、数据资产、AI治理——从人力结构到算力结构。',
    icon: 'Sparkles',
  },
];

// 客户端动画样式
function ClientStyles() {
  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-delay-100 { animation-delay: 100ms; }
        .animate-delay-200 { animation-delay: 200ms; }
        .animate-delay-300 { animation-delay: 300ms; }
        .animate-delay-400 { animation-delay: 400ms; }
        .animate-delay-500 { animation-delay: 500ms; }
        .animate-delay-600 { animation-delay: 600ms; }
        .animate-delay-700 { animation-delay: 700ms; }
        .animate-delay-800 { animation-delay: 800ms; }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .card-hover {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
        }
        
        .card-hover:hover .card-icon {
          transform: scale(1.1) rotate(5deg);
        }
        
        .card-hover:hover .card-arrow {
          transform: translateX(4px);
        }
        
        .card-hover:active {
          transform: scale(0.98);
        }
        
        /* 数字动画 */
        .counter-digit {
          display: inline-block;
          transition: transform 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

// 栏目卡片
function CategoryCard({ 
  category, 
  index 
}: { 
  category: { id: string; name: string; slug: string; description: string | null; icon: string | null };
  index: number;
}) {
  const colors = categoryColors[category.id || category.slug] || categoryColors['stable'];
  const IconComponent = categoryIcons[category.icon || 'Sparkles'] || Sparkles;
  
  return (
    <Link href={`/category/${category.slug}`} className="block">
      <Card className={`
        h-full relative overflow-hidden card-hover
        bg-[#12121a]/90 backdrop-blur-xl
        border ${colors.border} ${colors.glow}
        opacity-0 animate-fade-in-up
      `}
      style={{ animationDelay: `${800 + index * 100}ms` }}
      >
        {/* 背景渐变 */}
        <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} />
        
        {/* 顶部装饰线 */}
        <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${colors.text} to-transparent opacity-50`} />
        
        <CardHeader className="relative space-y-5 pt-8">
          {/* 数字标识 */}
          <div className="absolute top-4 right-4 text-xs font-mono text-white/20">
            0{index + 1}
          </div>
          
          {/* 图标 */}
          <div className={`${colors.text} p-4 rounded-2xl w-fit bg-[#1a1a25] border border-white/5 card-icon transition-transform duration-300`}>
            <IconComponent className="h-10 w-10" />
          </div>
          
          <CardTitle className="text-2xl font-bold text-white">
            {category.name}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="relative">
          <CardDescription className="text-gray-400 text-sm leading-relaxed">
            {category.description}
          </CardDescription>
          
          <div className={`mt-5 flex items-center text-sm font-medium ${colors.text} opacity-60 group-hover:opacity-100 transition-all duration-300`}>
            <span>探索内容</span>
            <ChevronRight className="ml-1 h-4 w-4 card-arrow transition-transform duration-300" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function HomePage() {
  // 客户端状态
  const [categories, setCategories] = useState<any[]>([]);
  const [latestArticles, setHotArticles] = useState<any[]>([]);
  const [hotAgents, setHotAgents] = useState<any[]>([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [catsRes, artsRes, agtsRes, allRes] = await Promise.all([
          fetch('/api/categories').then(r => r.json()),
          fetch('/api/articles?limit=3&sort=date').then(r => r.json()),
          fetch('/api/agents?status=published&limit=100').then(r => r.json()),
          fetch('/api/articles?status=published&limit=200').then(r => r.json()),
        ]);
        setCategories(Array.isArray(catsRes) ? catsRes : []);
        setHotArticles(artsRes.articles || []);
        setHotAgents(agtsRes.agents || []);
        const counts: Record<string, number> = {};
        (allRes.articles || []).forEach((a: any) => {
          counts[a.category_id] = (counts[a.category_id] || 0) + 1;
        });
        setCategoryCounts(counts);
        setTotalArticles((allRes.articles || []).length);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white relative overflow-hidden">
      <ClientStyles />
      <ParticleBackground />
      <MouseGlow />
      
      {/* 科技网格背景 */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="absolute top-0 left-1/4 right-1/4 h-[500px] bg-gradient-to-b from-cyan-500/10 via-blue-500/5 to-transparent blur-[100px] animate-pulse-glow" />
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-gradient-to-l from-purple-600/20 to-transparent rounded-full blur-[120px] animate-pulse-glow" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-gradient-to-r from-cyan-600/15 to-transparent rounded-full blur-[100px] animate-pulse-glow" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-0 left-0 right-0 h-[300px] bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
      </div>

      {/* 装饰性电路线条 */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <svg className="absolute top-20 left-10 w-32 h-32 text-cyan-500/20 animate-float" style={{ animationDelay: '0s' }}>
          <circle cx="16" cy="16" r="4" fill="currentColor" />
          <path d="M16 20 L16 60 M16 40 L50 40 M50 40 L50 80" stroke="currentColor" strokeWidth="1" fill="none" />
          <circle cx="50" cy="80" r="3" fill="currentColor" />
        </svg>
        <svg className="absolute bottom-40 right-20 w-40 h-40 text-purple-500/20 animate-float" style={{ animationDelay: '1.5s' }}>
          <circle cx="20" cy="20" r="4" fill="currentColor" />
          <path d="M20 24 L20 70 L60 70 M60 70 L60 100 L100 100" stroke="currentColor" strokeWidth="1" fill="none" />
          <circle cx="100" cy="100" r="3" fill="currentColor" />
        </svg>
      </div>

      {/* 主内容 */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative px-4 pt-32 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center">
              {/* 科技感标签 */}
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-500/30 bg-cyan-500/5 backdrop-blur-sm opacity-0 animate-fade-in-up animate-delay-100">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
                </span>
                <span className="text-sm font-medium text-cyan-400 tracking-wide">AI 时代组织效率提升指南</span>
              </div>
              
              {/* 主标题 */}
              <h1 className="mt-10 text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight opacity-0 animate-fade-in-up animate-delay-200">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400">
                  洞悉 AI 变革
                </span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                  重塑组织竞争力
                </span>
              </h1>
              
              {/* 装饰性下划线 */}
              <div className="mt-8 flex justify-center opacity-0 animate-fade-in-up animate-delay-300">
                <div className="w-48 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent animate-pulse-glow" />
              </div>
              
              {/* 统计数字 */}
              <div className="mt-12 flex justify-center gap-8 sm:gap-12 opacity-0 animate-fade-in-up animate-delay-400">
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-400">
                    <AnimatedCounter target={totalArticles} duration={1500} />
                  </div>
                  <div className="text-sm text-gray-500 mt-1">洞察文章</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-400">
                    <AnimatedCounter target={7} duration={1500} />
                  </div>
                  <div className="text-sm text-gray-500 mt-1">能力诊断</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">
                    <AnimatedCounter target={allCourses.length} duration={1500} />
                  </div>
                  <div className="text-sm text-gray-500 mt-1">行动工具</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-emerald-400">
                    <AnimatedCounter target={5} duration={1500} />
                  </div>
                  <div className="text-sm text-gray-500 mt-1">智能体</div>
                </div>
              </div>
              
              {/* 副标题数据 */}
              <p className="mt-6 text-center text-gray-400 opacity-0 animate-fade-in-up animate-delay-500">
                绩效·招聘·培训·会议·团队·数据·战略
              </p>
            </div>
          </div>
        </section>

        {/* 四大栏目入口 */}
        <section className="relative px-4 pb-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {staticCategories.map((category, index) => (
                <CategoryCard 
                  key={category.id} 
                  category={category} 
                  index={index} 
                />
              ))}
            </div>
          </div>
        </section>

        {/* 认知测评体系 */}
        <section className="relative px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-10 opacity-0 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-amber-500/30 bg-amber-500/5 backdrop-blur-sm">
                <ClipboardList className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-400 tracking-wide">认知测评体系</span>
              </div>
              <h2 className="mt-4 text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                测一测你的AI组织进化指数
              </h2>
              <p className="mt-2 text-sm text-gray-500">按职能选择测评，发现你的知识盲区</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {quizCategories.map((cat, index) => {
                const CatIcon = {
                  performance: Calculator,
                  recruitment: Users,
                  training: GraduationCap,
                  meeting: MessageSquare,
                  team: Heart,
                  data: Shield,
                  strategy: Target,
                }[cat.id] || ClipboardList;
                const catGradients = [
                  'from-rose-600/20 to-orange-600/20',
                  'from-blue-600/20 to-indigo-600/20',
                  'from-emerald-600/20 to-teal-600/20',
                  'from-violet-600/20 to-purple-600/20',
                  'from-pink-600/20 to-rose-500/20',
                  'from-cyan-600/20 to-blue-500/20',
                  'from-amber-600/20 to-yellow-500/20',
                ];
                const catBorders = [
                  'hover:border-rose-500/30',
                  'hover:border-blue-500/30',
                  'hover:border-emerald-500/30',
                  'hover:border-violet-500/30',
                  'hover:border-pink-500/30',
                  'hover:border-cyan-500/30',
                  'hover:border-amber-500/30',
                ];
                const catTexts = [
                  'text-rose-400', 'text-blue-400', 'text-emerald-400',
                  'text-violet-400', 'text-pink-400', 'text-cyan-400', 'text-amber-400',
                ];
                return (
                  <Link
                    key={cat.id}
                    href={`/quiz/${cat.id}`}
                    className={`group rounded-2xl border border-white/5 bg-[#12121a]/80 backdrop-blur p-5 transition-all ${catBorders[index]} hover:bg-[#1a1a25]/80 opacity-0 animate-fade-in-up`}
                    style={{ animationDelay: `${100 + index * 60}ms` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${catGradients[index]} border border-white/10 flex items-center justify-center group-hover:scale-110 transition-all duration-300 flex-shrink-0`}>
                        <CatIcon className={`h-6 w-6 ${catTexts[index]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white group-hover:text-amber-300 transition-colors">
                          {cat.name}
                        </h3>
                        <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                          {cat.description}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <Badge variant="outline" className={`text-[10px] border-white/10 ${catTexts[index]}`}>
                            {cat.quizzes.length} 套测评
                          </Badge>
                          <span className="text-[10px] text-gray-600">
                            {cat.quizzes[0]?.time || 5}min起
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 text-center">
              <Link href="/quiz" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors group">
                查看全部 {getTotalQuizCount()} 套测评
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* 入群引导 CTA */}
        <section className="relative px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="relative rounded-2xl bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-orange-500/10 border border-cyan-500/20 p-8 sm:p-12 text-center overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-500/5 via-transparent to-transparent" />
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  加入「算法组织实验室」
                </h2>
                <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                  获取AI组织进化方法论、参与测评讨论、解锁更多行动工具
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <img src="/wechat-qr.jpg" alt="扫码入群" className="w-32 h-32 rounded-lg" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2 text-cyan-400 mb-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z"/></svg>
                      <span className="font-medium">扫码加入学员群</span>
                    </div>
                    <ul className="text-sm text-gray-500 space-y-1">
                      <li>✓ 每周深度讨论</li>
                      <li>✓ 答案解析分享</li>
                      <li>✓ 独家方法论更新</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 智能体 + 最新文章 + 行动工具 */}
        <section className="relative px-4 py-16 sm:px-6 lg:px-8 border-t border-white/5">
          <div className="mx-auto max-w-6xl">
            
            {/* 智能体横向一排 */}
            <div className="mb-12">
              <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <Bot className="h-5 w-5 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">AI 智能体</h3>
                </div>
              
              <div className="grid grid-cols-5 gap-4">
                {hotAgents.map((agent, index) => {
                  const AgentIcon = agentIcons[agent.icon || 'Bot'] || Bot;
                  // 每个智能体独特的渐变色
                  const agentGradients = [
                    'from-orange-600/20 to-amber-600/20',   // 工作+AI
                    'from-yellow-500/20 to-lime-500/20',    // 你想学什么
                    'from-emerald-600/20 to-green-500/20',  // 你的课值多少钱
                    'from-pink-600/20 to-rose-500/20',      // 课程游戏化
                    'from-sky-600/20 to-blue-500/20',       // 小组讨论AI
                  ];
                  const agentBorders = [
                    'hover:border-orange-500/30',   // 工作+AI
                    'hover:border-yellow-500/30',   // 你想学什么
                    'hover:border-emerald-500/30',  // 你的课值多少钱
                    'hover:border-pink-500/30',     // 课程游戏化
                    'hover:border-sky-500/30',      // 小组讨论AI
                  ];
                  const agentIconBorders = [
                    'border-orange-500/20 group-hover:border-orange-500/40',
                    'border-yellow-500/20 group-hover:border-yellow-500/40',
                    'border-emerald-500/20 group-hover:border-emerald-500/40',
                    'border-pink-500/20 group-hover:border-pink-500/40',
                    'border-sky-500/20 group-hover:border-sky-500/40',
                  ];
                  const agentIconColors = [
                    'text-orange-400',
                    'text-yellow-400',
                    'text-emerald-400',
                    'text-pink-400',
                    'text-sky-400',
                  ];
                  const agentTextColors = [
                    'group-hover:text-orange-400',
                    'group-hover:text-yellow-400',
                    'group-hover:text-emerald-400',
                    'group-hover:text-pink-400',
                    'group-hover:text-sky-400',
                  ];
                  return (
                    <a key={agent.id} href={agent.coze_url} target="_blank" rel="noopener noreferrer" className="group">
                      <div className={`flex flex-col items-center gap-3 p-4 rounded-2xl bg-[#12121a]/80 backdrop-blur border border-white/5 transition-all ${agentBorders[index] || 'hover:border-purple-500/30'} hover:bg-[#1a1a25]/80 opacity-0 animate-fade-in-up`} style={{ animationDelay: `${700 + index * 80}ms` }}>
                        <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${agentGradients[index] || 'from-purple-600/20 to-blue-600/20'} border ${agentIconBorders[index] || 'border-purple-500/20 group-hover:border-purple-500/40'} flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                          <AgentIcon className={`h-7 w-7 ${agentIconColors[index] || 'text-purple-400'}`} />
                        </div>
                        <span className={`text-sm font-medium text-gray-300 ${agentTextColors[index] || 'group-hover:text-purple-400'} transition-colors text-center leading-tight`}>
                          {agent.name}
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* 最新文章 + 行动工具 并排 */}
            <div className="grid gap-10 lg:grid-cols-2">
              {/* 最新文章 */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-8 opacity-0 animate-fade-in-up animate-delay-800">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <BookOpen className="h-5 w-5 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">最新洞察</h3>
                  </div>
                  <Link href="/articles" className="text-sm text-gray-400 hover:text-cyan-400 flex items-center gap-1 transition-colors group">
                    查看全部 <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {latestArticles.map((article, index) => (
                    <Link key={article.id} href={`/articles/${article.id}`}>
                      <Card className="bg-[#12121a]/80 backdrop-blur border border-white/5 transition-all hover:border-cyan-500/30 hover:bg-[#1a1a25]/80 group opacity-0 animate-fade-in-up" style={{ animationDelay: `${900 + index * 100}ms` }}>
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            {article.cover_image && (
                              <div className="relative h-20 w-28 rounded-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                                <img 
                                  src={article.cover_image} 
                                  alt={article.title}
                                  className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#12121a]/80" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-white line-clamp-2 group-hover:text-cyan-400 transition-colors">
                                {article.title}
                              </h4>
                              <p className="mt-2 text-sm text-gray-500 line-clamp-1">
                                {article.summary}
                              </p>
                              <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                                  {article.category?.name}
                                </Badge>
                                {article.tags?.slice(0, 2).map((tag: string) => (
                                  <span key={tag} className="text-xs text-gray-600">#{tag}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                  
                  {isLoading && latestArticles.length === 0 && (
                    <Card className="bg-[#12121a]/80 backdrop-blur border border-white/5">
                      <CardContent className="p-10 text-center">
                        <div className="text-gray-400">加载中...</div>
                      </CardContent>
                    </Card>
                  )}
                  {!isLoading && latestArticles.length === 0 && (
                    <Card className="bg-[#12121a]/80 backdrop-blur border border-white/5">
                      <CardContent className="p-10 text-center">
                        <div className="text-gray-600">暂无文章内容</div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* 行动工具 */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-8 opacity-0 animate-fade-in-up animate-delay-800">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <GraduationCap className="h-5 w-5 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">行动工具</h3>
                  </div>
                  <Link href="/courses" className="text-sm text-gray-400 hover:text-emerald-400 flex items-center gap-1 transition-colors group">
                    查看全部 <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {allCourses.slice(0, 3).map((course, index) => (
                    <Link key={course.id} href={`/courses/${course.id}`}>
                      <Card className="bg-[#12121a]/80 backdrop-blur border border-white/5 transition-all hover:border-emerald-500/30 hover:bg-[#1a1a25]/80 group opacity-0 animate-fade-in-up" style={{ animationDelay: `${900 + index * 100}ms` }}>
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-emerald-600/20 to-cyan-600/20 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:border-emerald-500/40 group-hover:scale-110 transition-all duration-300">
                              <GraduationCap className="h-7 w-7 text-emerald-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-white line-clamp-2 group-hover:text-emerald-400 transition-colors">
                                {course.title}
                              </h4>
                              <p className="mt-2 text-sm text-gray-500 line-clamp-1">{course.summary}</p>
                              <div className="mt-3 flex items-center gap-3">
                                <Badge variant="secondary" className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                  {course.modules || 1}模块
                                </Badge>
                                {(course.tags || []).slice(0, 2).map(tag => (
                                  <span key={tag} className="text-xs text-gray-600">#{tag}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
